import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const PROVINCE_NAME_TO_CODE = {
  genova: 'GE', ge: 'GE',
  imperia: 'IM', im: 'IM',
  'la spezia': 'SP', spezia: 'SP', sp: 'SP',
  savona: 'SV', sv: 'SV',
};

const provinceToCode = (raw) => {
  if (!raw) return null;
  const k = String(raw).trim().toLowerCase();
  if (PROVINCE_NAME_TO_CODE[k]) return PROVINCE_NAME_TO_CODE[k];
  const u = String(raw).trim().toUpperCase();
  return u.length === 2 ? u : null;
};

/**
 * Quick edit dialog for ROSS1000/ISTAT credentials only.
 * Loads property by ID, lets user fix has_ross1000_delegation, mode, structure_code, CIR.
 * On save, calls onSaved callback.
 */
const QuickEditRoss1000Dialog = ({ propertyId, open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [property, setProperty] = useState(null);
  const [groupCreds, setGroupCreds] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    has_ross1000_delegation: false,
    ross1000_credentials_mode: null,
    ross1000_username: '',
    ross1000_password: '',
    ross1000_structure_code: '',
    ross1000_cir_from_delegation: '',
  });

  useEffect(() => {
    if (open && propertyId) {
      loadAll();
    }
  }, [open, propertyId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const [pRes, gRes] = await Promise.all([
        axios.get(`${API}/api/properties/${propertyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/api/ross1000/group-credentials`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const p = pRes.data;
      setProperty(p);
      const map = {};
      (gRes.data?.credentials || []).forEach((c) => { if (c.province_code) map[c.province_code] = c; });
      setGroupCreds(map);
      setForm({
        has_ross1000_delegation: p.has_ross1000_delegation || false,
        ross1000_credentials_mode: p.ross1000_credentials_mode || null,
        ross1000_username: p.ross1000_username || '',
        ross1000_password: p.ross1000_password || '',
        ross1000_structure_code: p.ross1000_structure_code || '',
        ross1000_cir_from_delegation: p.ross1000_cir_from_delegation || p.cir || '',
      });
    } catch (err) {
      toast.error('Errore caricamento immobile');
    } finally {
      setLoading(false);
    }
  };

  const groupForProvince = property
    ? groupCreds[provinceToCode(property.province)]
    : null;

  const effectiveMode = form.ross1000_credentials_mode || (groupForProvince ? 'group' : 'individual');
  const effectiveUsername = effectiveMode === 'group' && groupForProvince
    ? groupForProvince.username
    : form.ross1000_username;
  const effectivePassword = effectiveMode === 'group' && groupForProvince
    ? groupForProvince.password
    : form.ross1000_password;

  const handleSave = async () => {
    if (!property) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      const payload = {
        has_ross1000_delegation: form.has_ross1000_delegation,
        ross1000_credentials_mode: form.ross1000_credentials_mode,
        ross1000_username: form.ross1000_username || null,
        ross1000_password: form.ross1000_password || null,
        ross1000_structure_code: form.ross1000_structure_code || null,
        ross1000_cir_from_delegation: form.ross1000_cir_from_delegation || null,
      };
      await axios.put(`${API}/api/properties/${propertyId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Configurazione salvata');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            🛠️ Sistema configurazione ROSS1000
            {property && (
              <span className="block text-sm font-normal text-slate-500 mt-1">
                {property.name} · {property.region} · {property.city} ({property.province})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : property ? (
          <div className="space-y-4">
            {/* Toggle delega */}
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded">
              <input
                type="checkbox"
                checked={form.has_ross1000_delegation}
                onChange={(e) =>
                  setForm({ ...form, has_ross1000_delegation: e.target.checked })
                }
                className="h-4 w-4"
                data-testid="quick-edit-delegation"
              />
              <span className="text-sm font-medium">
                {form.has_ross1000_delegation
                  ? '✅ Delega ROSS1000 attiva (verrà inviato)'
                  : '❌ Delega ROSS1000 disattivata (NON verrà inviato)'}
              </span>
            </div>

            {form.has_ross1000_delegation && (
              <>
                {/* Mode selector */}
                <div>
                  <Label className="mb-2 block">Tipo Credenziali</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <label
                      className={`p-3 rounded border cursor-pointer ${
                        effectiveMode === 'group'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:bg-slate-50'
                      } ${!groupForProvince ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="qmode"
                        value="group"
                        checked={effectiveMode === 'group'}
                        disabled={!groupForProvince}
                        onChange={() =>
                          setForm({ ...form, ross1000_credentials_mode: 'group' })
                        }
                        className="mr-2"
                        data-testid="quick-edit-mode-group"
                      />
                      <span className="text-sm font-medium">👥 Account di Gruppo</span>
                      <div className="text-xs text-slate-500 mt-1">
                        {groupForProvince
                          ? `Provincia ${groupForProvince.province}`
                          : `Nessun account per "${property.province}"`}
                      </div>
                    </label>
                    <label
                      className={`p-3 rounded border cursor-pointer ${
                        effectiveMode === 'individual'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="qmode"
                        value="individual"
                        checked={effectiveMode === 'individual'}
                        onChange={() =>
                          setForm({ ...form, ross1000_credentials_mode: 'individual' })
                        }
                        className="mr-2"
                        data-testid="quick-edit-mode-individual"
                      />
                      <span className="text-sm font-medium">🔑 Credenziali Individuali</span>
                      <div className="text-xs text-slate-500 mt-1">Inserisci user/password</div>
                    </label>
                  </div>
                </div>

                {/* Credentials display */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Username</Label>
                    <Input
                      type="text"
                      name="ross1000-user-nofill"
                      autoComplete="off"
                      value={effectiveUsername || ''}
                      readOnly={effectiveMode === 'group'}
                      onChange={(e) =>
                        setForm({ ...form, ross1000_username: e.target.value })
                      }
                      className={effectiveMode === 'group' ? 'bg-slate-100' : ''}
                      data-testid="quick-edit-username"
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="ross1000-pass-nofill"
                        autoComplete="new-password"
                        value={effectivePassword || ''}
                        readOnly={effectiveMode === 'group'}
                        onChange={(e) =>
                          setForm({ ...form, ross1000_password: e.target.value })
                        }
                        className={`pr-9 ${effectiveMode === 'group' ? 'bg-slate-100' : ''}`}
                        data-testid="quick-edit-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Structure code + CIR */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Codice Struttura ROSS1000</Label>
                    <Input
                      type="text"
                      value={form.ross1000_structure_code}
                      onChange={(e) =>
                        setForm({ ...form, ross1000_structure_code: e.target.value })
                      }
                      placeholder="Es. L09681"
                      data-testid="quick-edit-structure"
                    />
                  </div>
                  <div>
                    <Label>CIR da Delega</Label>
                    <Input
                      type="text"
                      value={form.ross1000_cir_from_delegation}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          ross1000_cir_from_delegation: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="Es. 010025-LT-7307"
                      className="uppercase"
                      data-testid="quick-edit-cir"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="quick-edit-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickEditRoss1000Dialog;
